/*
 * @Author: Ryan
 * @Date: 2026-01-14
 * @Description: Testimonials section - What Users Say About AI Video Generator
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'Saved Me So Much Time',
    content:
      'FalcoCut helps me make videos in minutes instead of days. I can test ideas fast and finish more projects each week. The workflow feels smooth, and I never get stuck. It has become my go-to tool for quick content.',
    role: 'Social Media Manager',
    name: 'Maya Chen',
  },
  {
    title: 'Perfect for Quick Content',
    content:
      'I use FalcoCut for daily posts, and it never slows me down. The videos look clean, and the workflow is simple. I can create new clips even on busy days. It keeps my content fresh without extra effort.',
    role: 'Content Creator',
    name: 'Jordan Lee',
  },
  {
    title: 'Great for Product Demos',
    content:
      'My team uses FalcoCut to show new products without filming. The videos look sharp, and we save a lot of money. We can update demos anytime with a simple prompt. It helps us launch faster and stay ahead.',
    role: 'E-commerce Founder',
    name: 'Sam Rivera',
  },
  {
    title: 'I Finally Feel Confident Making Videos',
    content:
      "I'm not a video editor, but FalcoCut makes the process simple. I can create clear explainers for my students in minutes. The steps are easy to follow, and the results look great. It has made my lessons more fun and visual.",
    role: 'High School Teacher',
    name: 'Emily Park',
  },
  {
    title: 'Our Team Works Faster Now',
    content:
      'We used to spend weeks on one video. Now we make many in the same time. The speed helps us hit deadlines without stress. My team feels more creative because we can try more ideas.',
    role: 'Marketing Lead',
    name: 'Daniel Brooks',
  },
  {
    title: 'My Go-To Creative Tool',
    content:
      'FalcoCut helps me turn ideas into videos with almost no effort. I love how easy it is to adjust scenes and styles. It gives me room to explore new looks and concepts. I use it for both client work and personal projects.',
    role: 'Freelance Designer',
    name: 'Aisha Patel',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-gray-900 mb-3 pad:mb-4">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-gray-900 text-sm pad:text-base">{role}</p>
        <p className="text-gray-500 text-sm">{name}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          What Our Customers Say
        </h2>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
